import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === "string" && s.next.startsWith("/") && !s.next.startsWith("//") ? s.next : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Login — Comm42 SA" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const { next } = Route.useSearch();
  const [mounted, setMounted] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">
        Loading…
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        if (!data.session) throw new Error("Login accepted, but no session was created.");
        await new Promise<void>((resolve, reject) => {
          let subscription: { unsubscribe: () => void } | undefined;
          const timeout = window.setTimeout(() => {
            subscription?.unsubscribe();
            reject(new Error("Login session did not become ready. Please try again."));
          }, 5000);
          const authListener = supabase.auth.onAuthStateChange((_event, session) => {
            if (!session) return;
            window.clearTimeout(timeout);
            subscription?.unsubscribe();
            resolve();
          });
          subscription = authListener.data.subscription;
          supabase.auth.getSession().then(({ data: sessionData }) => {
            if (!sessionData.session) return;
            window.clearTimeout(timeout);
            subscription?.unsubscribe();
            resolve();
          }).catch((error) => {
            window.clearTimeout(timeout);
            subscription?.unsubscribe();
            reject(error);
          });
        });
      toast.success(t("auth.loginOk"));
      window.location.assign(next ?? "/admin/news");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell>
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-6 py-32">
        <h1 className="font-serif text-4xl">{t("auth.loginTitle")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.subtitle")}</p>
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="password">{t("auth.password")}</Label>
            <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy ? "..." : t("auth.loginBtn")}
          </Button>
        </form>
        <Link to="/" className="mt-8 text-center text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
          ← {t("notFound.home")}
        </Link>
      </div>
    </PageShell>
  );
}